import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, message, Typography, Tag } from "antd";
import {
  FileText,
  Video,
  Link as LinkIcon,
  ChevronLeft,
  Eye,
  ArrowLeft,
} from "lucide-react";
import { getSubmissionById } from "../services/submissionService";

const { Text } = Typography;

interface Submission {
  _id: string;
  projectName: string;
  createdAt: string;
  updatedAt: string;
  teamMembers: Array<{ name: string; email: string }>;
  status: string;
  projectDescription: string;
  demoLink: string;
  documentUrl?: string;
  documentOriginalName?: string;
  supportingLinks?: Array<{ url: string; description?: string }>;
  supportingContent?: Array<{
    title: string;
    value: string;
  }>;
  industry?: string;
  googleTechnologies?: string[];
  otherTechnologies?: string[];
  lessonsLearned?: string;
  selfReflection?: string;
  firstTimeUsingGcp?: boolean;
  leaderEmail?: string;
  leaderName?: string;
  teamName?: string;
}

const SubmissionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadSubmission();
    }
  }, [id]);

  const loadSubmission = async () => {
    try {
      setLoading(true);
      const response = await getSubmissionById(id!);
      if (response.success) {
        setSubmission(response.data);
      } else {
        message.error("Failed to load submission details");
        navigate("/submissions");
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to load submission details"
      );
      navigate("/submissions");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="p-6">
        <Button
          icon={<ArrowLeft />}
          onClick={() => navigate("/submissions")}
          className="mb-4"
        >
          Back
        </Button>
        <div className="text-center py-12">
          <Text type="secondary">Submission not found</Text>
        </div>
      </div>
    );
  }

  const allTechnologies = [
    ...(submission.googleTechnologies || []),
    ...(submission.otherTechnologies || []),
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          type="text"
          icon={<ChevronLeft size={16} />}
          onClick={() => navigate("/submissions")}
          className="mb-4 p-2 text-black text-base font-medium"
        >
          Back
        </Button>
        {/*
        <div className="mt-2 flex items-center gap-4">
          <Tag color={submission.status === "submitted" ? "green" : "blue"}>
            {submission.status === "auto_submitted" ? "Auto Submitted" : "Submitted"}
          </Tag>
          <Text type="secondary">
            Submitted on {dayjs(submission.createdAt).format("MMMM DD, YYYY [at] h:mm A")}
          </Text>
        </div> */}
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-[8px]  border border-gray-200 p-6 mb-6 w-full max-w-4xl mx-auto flex flex-col gap-5">
        <div className="text-2xl font-semibold text-gray-900 ">
          {submission.projectName.charAt(0).toUpperCase() +
            submission.projectName.slice(1)}
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
          {submission.teamName || "Individual Submission"}
          </div>

          {submission.teamMembers && submission.teamMembers.length > 0 ? (
            <div className="grid grid-cols-2 ">
              <div className="flex flex-col gap-1">
                <span className="text-base font-medium text-[#111112]">
                  {submission.teamMembers[0]?.name || submission.leaderName || "N/A"} <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Leader</span>
                </span>
                <span className="text-sm font-normal text-[#475467]">
                  {submission.teamMembers[0]?.email || submission.leaderEmail || "N/A"}
                </span>
              </div>
              {submission.teamMembers[1] && (
                <div className="flex flex-col gap-1">
                  <span className="text-base font-medium text-[#111112]">
                    {submission.teamMembers[1].name} <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Member</span>
                  </span>
                  <span className="text-sm font-normal text-[#475467]">
                    {submission.teamMembers[1].email}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-base font-medium text-[#111112]">
                {submission.leaderName || "N/A"} <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Participant</span>
              </span>
              <span className="text-sm font-normal text-[#475467]">
                {submission.leaderEmail || "N/A"}
              </span>
            </div>
          )}
        </div>
        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Project Description
          </div>

          <span className="text-base font-normal text-[#475467]">
            {submission.projectDescription}
          </span>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Lessons Learned
          </div>

          <span className="text-base font-normal text-[#475467]">
            {submission.lessonsLearned}
          </span>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Self Reflection
          </div>

          <span className="text-base font-normal text-[#475467]">
            {submission.selfReflection}
          </span>
        </div>
        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Others Details
          </div>

          <div className="grid grid-cols-2 ">
            <div className="flex flex-col gap-1">
              <span className="text-base font-medium text-[#111112]">
                Industry
              </span>
              <span className="text-sm font-normal text-[#475467]">
                {submission.industry}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-base font-medium text-[#111112]">
                First Time Using GCP
              </span>
              <span className="text-sm font-normal text-[#475467]">
                {submission.firstTimeUsingGcp ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Technologies (Google and Others)
          </div>

          <div className="flex flex-wrap gap-2">
            {allTechnologies.map((tech, index) => (
              <Tag key={index} className="px-3 py-1 text-sm">
                {tech}
              </Tag>
            ))}
          </div>
        </div>

        <div className="bg-[#F9FAFB] rounded-[4px] p-4 border border-gray-100 flex flex-col gap-4">
          <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
            Submission Materials
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-start gap-3">
                  <div className="h-full flex items-start justify-start">
                    <Video className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <Text strong>Demo Video</Text>
                    <br />
                    <Text type="secondary" className="text-sm">
                      {submission.demoLink}
                    </Text>
                  </div>
                </div>
                <div
                  onClick={() => window.open(submission.demoLink, "_blank")}
                  className="cursor-pointer"
                >
                  <Eye />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 w-full justify-between">
                <div className="flex items-start gap-3">
                  <div className="h-full flex items-start justify-start">
                    <FileText className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <Text strong>Project Documentation</Text>
                    <br />
                    <Text type="secondary" className="text-sm">
                      {submission.documentOriginalName}
                    </Text>
                  </div>
                </div>
                <div
                  onClick={() => window.open(submission.documentUrl, "_blank")}
                  className="cursor-pointer"
                >
                  <Eye />
                </div>
              </div>
            </div>

            {submission.supportingLinks &&
              submission.supportingLinks.length > 0 && (
                <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg bg-[#F9FAFB]">
                  <div className="h-full flex items-center justify-start gap-2">
                    <LinkIcon className="w-5 h-5 text-gray-500" />
                    <Text strong>Supporting Links</Text>
                  </div>
                  {submission.supportingLinks.map((link, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex flex-col  gap-2 w-full justify-between">
                        <div className="flex items-center justify-between w-full gap-3">
                          <div>
                            <Text strong>
                              {link.description || "Supporting Link"}
                            </Text>
                            <br />
                            <Text type="secondary" className="text-sm">
                              {link.url}
                            </Text>
                          </div>

                          <div
                            onClick={() => window.open(link.url, "_blank")}
                            className="cursor-pointer"
                          >
                            <Eye />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {submission.supportingContent &&
              Array.isArray(submission.supportingContent) &&
              submission.supportingContent.length > 0 && (
                <div className="rounded-[4px] p-4 border border-gray-200 flex flex-col gap-4 bg-[#F9FAFB]">
                  <div className="text-base font-medium text-gray-900 border-b border-gray-100 pb-2">
                    Supporting Content
                  </div>
                  <div className="flex flex-col gap-3">
                    {submission.supportingContent.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-1 p-3 bg-white rounded border border-gray-100"
                      >
                        <Text strong className="text-sm text-gray-900">
                          {item.title}
                        </Text>
                        <Text className="text-sm text-[#475467] whitespace-pre-wrap">
                          {item.value}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetails;
